import React, { useContext, useState } from 'react';
import { createURL } from '@sm/utils';
import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Grid } from '@wds/grid';
import { Input } from '@wds/input';
import { Checkbox } from '@wds/checkbox';
import { StaticContext, Recaptcha, useRecaptcha } from '@sm/webassets';
import classnames from 'classnames';

import { Button as CoreButton } from '../core/Button';
import useStyles from './useStyles';

export type Props = {
  className: string;
  ctaFormSubmit: string;
  ctaFormEmail: string;
  ctaFormEmailPlaceholder: string;
  ctaFormNewsletterOptOut: string;
  ctaFormPassword: string;
  ctaFormPasswordPlaceholder: string;
  ctaFormPrivacyConsent: React.ReactNode;
  ctaFacebook?: string;
  ctaGoogle?: string;
  /** Disclaimer HTML string */
  disclaimer: string;
  utSource?: string;
  utSource2?: string;
  showThirdPartySignUpActions?: boolean;
  showDisclaimer?: boolean;
  isEnterpriseThankYou?: boolean;
};

/** Type-wrapper for `Typography` to allow use of `dangerouslySetInnerHTML`  */
const TypographyWithInnerHTML = Typography as React.ForwardRefExoticComponent<
  Omit<React.ComponentProps<typeof Typography>, 'children'> & {
    dangerouslySetInnerHTML: { __html: string };
    children?: never;
  }
>;

const HeroFormCTA = ({
  className,
  ctaFormEmail,
  ctaFormEmailPlaceholder,
  ctaFormNewsletterOptOut,
  ctaFormPassword,
  ctaFormPasswordPlaceholder,
  ctaFormPrivacyConsent,
  ctaFormSubmit,
  ctaFacebook = 'FACEBOOK',
  ctaGoogle = 'GOOGLE',
  disclaimer,
  utSource = '',
  utSource2 = '',
  showThirdPartySignUpActions = true,
  showDisclaimer = true,
  isEnterpriseThankYou,
}: Props): React.ReactElement => {
  const utSource3 = 'HeroFormCTA';
  const {
    environment: { countryCode },
  } = useContext(StaticContext);

  const { hardRule, enterpriseThankYouForm, ctaFormField, ctaLabel, visuallyHidden, facebookButton, googleButton } =
    useStyles({
      isEnterpriseThankYou,
    });

  const containerClasses = classnames('sm-hero-form-cta', className);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setEmail(e.target.value);
  };
  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPassword(e.target.value);
  };

  const submitCallback = (): void => {
    (document.getElementById('signup_form') as HTMLFormElement).submit();
  };

  const recaptchaContext = useRecaptcha({
    actionName: 'user_signup',
    successCallback: submitCallback,
    siteKey: undefined, // set via StaticContext
    useEnterprise: undefined, // set via StaticContext
    errorCallback: undefined,
  });

  return (
    <Box className={containerClasses} pt={7} data-testid="HeroFormCTA">
      <form
        id="signup_form"
        method="post"
        action={createURL('/user/sign-up/', {
          ut_source: utSource,
          ut_source2: utSource2,
          ut_source3: utSource3,
          ut_ctatext: ctaFormSubmit,
        })}
        className={isEnterpriseThankYou ? enterpriseThankYouForm : undefined}
        data-testid="HeroFormCTA__Form"
      >
        <Recaptcha
          greToken={recaptchaContext.greToken}
          showError={recaptchaContext.showError}
          setShowError={recaptchaContext.setShowError}
        />
        <input type="hidden" name="username" className="input-username" value={email} />
        <input type="hidden" name="password_confirm" className="input-password_confirm" value={password} />
        <Box element="div" className={ctaFormField}>
          <label className={isEnterpriseThankYou ? visuallyHidden : ctaLabel} htmlFor="email-input">
            <Typography component="span" variant="bodySm">
              {ctaFormEmail}
            </Typography>
          </label>
          <Input
            id="email-input"
            type="email"
            name="email"
            placeholder={ctaFormEmailPlaceholder}
            required
            stretched
            onChange={handleEmailChange}
          />
        </Box>
        <Box element="div" className={ctaFormField}>
          <label className={isEnterpriseThankYou ? visuallyHidden : ctaLabel} htmlFor="password-input">
            <Typography component="span" variant="bodySm">
              {ctaFormPassword}
            </Typography>
          </label>
          <Input
            id="password-input"
            type="password"
            name="password"
            placeholder={ctaFormPasswordPlaceholder}
            required
            stretched
            onChange={handlePasswordChange}
          />
        </Box>
        {countryCode.toLowerCase() === 'de' && (
          <>
            <Box element="div" className={ctaFormField}>
              <Checkbox label={ctaFormPrivacyConsent} id="privacy-consents" name="privacy-consents" required />
            </Box>
            <Box element="div" className={ctaFormField}>
              <Checkbox label={ctaFormNewsletterOptOut} id="subscribe-to-newsletter" name="subscribe-to-newsletter" />
            </Box>
          </>
        )}
        <Button size="lg" stretched onClick={recaptchaContext.fireGRE}>
          {ctaFormSubmit}
        </Button>
      </form>
      <Box mb={4} mt={4} className={hardRule}>
        <></>
      </Box>
      {showThirdPartySignUpActions && (
        <Grid noBleeds>
          <Grid.Item xs={6}>
            {/* @todo: We will need to connect with Henry W to discuss how to convert this over */}
            <CoreButton
              className={facebookButton}
              stretched
              href={createURL('/user/third-party/', {
                third_party: 'facebook',
                action: 'sign_up',
              })}
            >
              <span>{ctaFacebook}</span>
            </CoreButton>
          </Grid.Item>
          <Grid.Item xs={6}>
            {/* @todo: We will need to connect with Henry W to discuss how to convert this over */}
            <CoreButton
              className={googleButton}
              stretched
              href={createURL('/user/third-party/', {
                third_party: 'google',
                action: 'sign_up',
              })}
            >
              <span>{ctaGoogle}</span>
            </CoreButton>
          </Grid.Item>
        </Grid>
      )}
      {showDisclaimer && (
        <Box mt={3}>
          <TypographyWithInnerHTML
            align="center"
            component="div"
            variant="bodySm"
            color="darkMuted"
            dangerouslySetInnerHTML={{ __html: `${disclaimer}` }} // eslint-disable-line jam3/no-sanitizer-with-danger
          />
        </Box>
      )}
    </Box>
  );
};

export default HeroFormCTA;
